import React from 'react';
import { useInvokeExtension } from '../web-client';
import { useTracingContext } from '../error-reporting';
import { ThreeLOPrompt } from '../components/threeLOPrompt';
import type { IframeProps } from '../custom-ui/iframe/types';
import type { BridgeServices, ModalController } from './types';

type UseBridgeServicesInput = Pick<IframeProps, 'contextIds' | 'extension' | 'apolloClient' | 'getContextToken'>;

/**
 * Assembles the stable host-side capabilities required by bridge methods.
 *
 * All returned values are stable references for the lifetime of the component:
 * - `invoker` is memoised by `useInvokeExtension` (Apollo mutation hook)
 * - `tracing` is a stable context value
 * - `modalController` is passed in by the caller and must itself be stable
 *   (backed by a ref).
 *
 * Throws at mount time if required capabilities are missing, so bridge methods
 * can use all services without defensive optional chaining.
 *
 * This hook must be called before `useBridgeContext` so the services object
 * is ready to be passed to `BridgeContextObject` at construction time.
 */
export const useBridgeServices = (
	props: UseBridgeServicesInput,
	modalController: ModalController,
): BridgeServices => {
	const invoker = useInvokeExtension(props.contextIds, props.extension.id, {
		client: props.apolloClient,
	});
	const tracing = useTracingContext();

	if (!tracing) {
		throw new Error(
			'WorkerWebRuntime: TracingProvider not found in the component tree. ' +
			'Wrap WorkerWebRuntime with <TracingProvider> to enable bridge method support.',
		);
	}

	return {
		invoker,
		getContextToken: (props.getContextToken ?? nullGetContextToken),
		tracing: tracing,
		modalController,
		onThreeLO: makeOnThreeLO(modalController),
	};
};

// TODO: currently sandbox does not support getContextToken (unlike other products),
// so we provide a noop implementation to avoid having to guard every bridge method that uses it.
const nullGetContextToken = (() => undefined) as unknown as BridgeServices['getContextToken'];

/**
 * Factory that produces the `onThreeLO` service callback for BridgeServices.
 *
 * Separating this from `useRuntimeModalController` keeps `ModalController`
 * generic and free from domain-specific knowledge.  The returned function shows
 * the 3LO consent prompt via `modalController.show()` and resolves the returned
 * Promise after the user grants consent and `retry()` completes.
 *
 * The returned callback must be kept stable by the caller (e.g. stored in a ref)
 * because it is placed in BridgeServices and captured by bridge method closures.
 */
export const makeOnThreeLO = (
	modalController: ModalController,
): BridgeServices['onThreeLO'] =>
	(authUrl: string, retry: () => Promise<void>): Promise<void> =>
		new Promise((resolve, reject) => {
			modalController.show(
				React.createElement(ThreeLOPrompt, {
					authUrl,
					onSuccess: async () => {
						modalController.hide();
						try {
							await retry();
							resolve();
						} catch (e) {
							reject(e);
						}
					},
				}),
			);
		});
